// BillingReport — money in, money out, what's still owed.
//
// Two data sources merged into one report:
//   • /payments       — every individual transaction (the revenue ledger)
//   • /reservations   — for outstanding balances by booking
// The payment ledger drives the daily-revenue chart, the per-method
// breakdown, and the itemised "Payments Received" table.  Reservations
// power the outstanding balances list.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReportShell from "./ReportShell";
import { Panel, StatusBadge, Loading, fmtMoney } from "./OverviewReport";
import { StatTile, LineChart, BarChart } from "../../../components/charts";
import { reservationsUrl, paymentsUrl } from "../../../BaseURL";

export default function BillingReport({ range, onRangeChange }) {
  const [reservations, setReservations] = useState([]);
  const [payments,     setPayments]     = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { from: range.from || undefined, to: range.to || undefined };
        const [resvRes, payRes] = await Promise.all([
          axios.get(reservationsUrl, { params }),
          axios.get(paymentsUrl,     { params }),
        ]);
        if (cancelled) return;
        setReservations(resvRes.data?.results || []);
        setPayments(payRes.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const stats = useMemo(() => {
    const booked      = reservations.reduce((s, r) => s + (Number(r.total_amount) || 0), 0);
    // Revenue is taken from the payment ledger so it reflects what was
    // actually collected in the date range, not what reservations starting
    // in the range have on their paid_amount running total.
    const revenue     = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const outstanding = reservations
      .filter(r => r.status !== "cancelled")
      .reduce((s, r) => s + Math.max(0, Number(r.balance) || 0), 0);

    const paidCount    = reservations.filter(r => r.payment_status === "paid").length;
    const partialCount = reservations.filter(r => r.payment_status === "partial").length;
    const unpaidCount  = reservations.filter(r => r.payment_status === "unpaid").length;

    const byMethod = aggregate(payments, p => p.method, p => Number(p.amount) || 0);
    const byDay    = bucketByDay(payments);

    const debtors = reservations
      .filter(r => Number(r.balance) > 0 && r.status !== "cancelled")
      .sort((a, b) => Number(b.balance) - Number(a.balance))
      .slice(0, 25);

    return {
      booked, revenue, outstanding,
      paidCount, partialCount, unpaidCount,
      byMethod, byDay, debtors,
    };
  }, [reservations, payments]);

  // CSV export = the payment ledger.  This is what the user actually wants
  // to file away ("here are all the transactions for the period").
  const csv = useMemo(() => ({
    headers: ["Received At","Reservation","Guest","Room","Method","Reference","Amount","Received By"],
    rows: payments.map(p => [
      fmtDateTime(p.received_at),
      p.reservation_code || "",
      p.guest_name || "",
      p.room_number || "",
      p.method || "",
      p.reference || "",
      Number(p.amount).toFixed(2),
      p.received_by_name || "",
    ]),
  }), [payments]);

  return (
    <ReportShell
      title="Billing Report"
      subtitle="Revenue collected, payments received and outstanding balances"
      range={{ ...range, onChange: onRangeChange }}
      csv={csv}
      filename="billing"
    >
      {loading ? <Loading/> : (
        <>
          <div className="kpi-row">
            <StatTile icon="💵" label="Revenue Collected" value={fmtMoney(stats.revenue)}     sub={`${payments.length} payment${payments.length === 1 ? "" : "s"}`} accent="#4a7c59" />
            <StatTile icon="📊" label="Booked Total"      value={fmtMoney(stats.booked)}      sub={`${reservations.length} reservations`} />
            <StatTile icon="⚠️" label="Outstanding"       value={fmtMoney(stats.outstanding)} sub={`${stats.partialCount + stats.unpaidCount} unsettled`} accent="#a93226" />
            <StatTile icon="✅" label="Fully Paid"        value={stats.paidCount}             sub={`${stats.partialCount} partial · ${stats.unpaidCount} unpaid`} accent="#2d6a4f" />
            <StatTile icon="📈" label="Avg Payment"       value={fmtMoney(payments.length ? stats.revenue / payments.length : 0)} accent="#8a6a30" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 18, marginTop: 16 }}>
            <Panel title="Revenue Collected per Day">
              <LineChart data={stats.byDay} valueFormat={(n) => `GH₵ ${Math.round(n)}`} />
            </Panel>
            <Panel title="Collected by Payment Method">
              <BarChart data={stats.byMethod} valueFormat={fmtMoney} />
            </Panel>
          </div>

          <Panel title={`Payments Received (${payments.length})`} style={{ marginTop: 18 }}>
            <table className="tbl" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>Reservation</th>
                  <th>Guest</th>
                  <th>Room</th>
                  <th>Method</th>
                  <th>Reference</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th>Received By</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td>{fmtDateTime(p.received_at)}</td>
                    <td style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}>
                      {p.reservation_code}
                    </td>
                    <td>{p.guest_name || "—"}</td>
                    <td>{p.room_number || "—"}</td>
                    <td><MethodBadge method={p.method}/></td>
                    <td style={{ color: "#6b5142", fontSize: 12 }}>{p.reference || "—"}</td>
                    <td style={{ textAlign: "right", color: "#2d6a4f", fontWeight: 500 }}>
                      {fmtMoney(p.amount)}
                    </td>
                    <td style={{ fontSize: 12, color: "#6b5142" }}>{p.received_by_name || "—"}</td>
                  </tr>
                ))}
                {payments.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#8a7060", fontStyle: "italic" }}>
                    No payments recorded in this range.
                  </td></tr>
                )}
              </tbody>
              {payments.length > 0 && (
                <tfoot>
                  <tr style={{ background: "rgba(180,140,80,.06)" }}>
                    <td colSpan={6} style={{ padding: "10px 12px", fontWeight: 600, color: "#2c1f14", textAlign: "right" }}>
                      Total Collected
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600, color: "#2d6a4f" }}>
                      {fmtMoney(stats.revenue)}
                    </td>
                    <td/>
                  </tr>
                </tfoot>
              )}
            </table>
          </Panel>

          <Panel title={`Outstanding Balances (top ${stats.debtors.length})`} style={{ marginTop: 18 }}>
            <table className="tbl" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Code</th><th>Guest</th><th>Room</th><th>Status</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th style={{ textAlign: "right" }}>Paid</th>
                  <th style={{ textAlign: "right" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {stats.debtors.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}>{r.code}</td>
                    <td>{r.guest_name}</td>
                    <td>{r.room_number || "—"}</td>
                    <td><StatusBadge status={r.status}/></td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(r.total_amount)}</td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(r.paid_amount)}</td>
                    <td style={{ textAlign: "right", color: "#a93226", fontWeight: 500 }}>
                      {fmtMoney(r.balance)}
                    </td>
                  </tr>
                ))}
                {stats.debtors.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#2d6a4f", fontWeight: 500 }}>
                    ✓ No outstanding balances in this range.
                  </td></tr>
                )}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </ReportShell>
  );
}

// Tints each payment method so the table scans at a glance — green for
// cash-in-hand, gold for digital channels.  Soft pill, not loud.
function MethodBadge({ method }) {
  const m = String(method || "").toLowerCase();
  const tone =
    m.includes("cash")     ? { bg: "rgba(74,124,89,.14)",  fg: "#2d6a4f" } :
    m.includes("transfer") ? { bg: "rgba(58,123,213,.14)", fg: "#3a7bd5" } :
    m.includes("mobile")   ? { bg: "rgba(230,126,34,.14)", fg: "#a85a14" } :
    m.includes("card")     ? { bg: "rgba(138,106,48,.14)", fg: "#6b5142" } :
                             { bg: "rgba(180,140,80,.10)", fg: "#6b5142" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      fontSize: 11, fontWeight: 500, background: tone.bg, color: tone.fg,
    }}>{method || "—"}</span>
  );
}

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

function aggregate(list, keyFn, valueFn) {
  const m = new Map();
  for (const r of list) {
    const k = keyFn(r) || "—";
    m.set(k, (m.get(k) || 0) + (valueFn ? valueFn(r) : 1));
  }
  return Array.from(m.entries()).map(([label, value]) => ({ label, value }));
}

// Bucket payments by the *day they were received* (not the booking's
// check-in date) so the line chart reflects daily cash flow.
function bucketByDay(payments) {
  const m = new Map();
  for (const p of payments) {
    const day = String(p.received_at || "").slice(0, 10);
    if (!day) continue;
    m.set(day, (m.get(day) || 0) + (Number(p.amount) || 0));
  }
  return Array.from(m.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, value]) => ({
      label: new Date(`${day}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
      value,
    }));
}
